<template>
  <main
    style="padding-top: 80px"
    class="team-area"
    :class="{ 'green-area': currentTab === 'TeamLeadArea' || currentTab === 'MyDevelopment' }"
  >
    <div v-if="!isLoading">
      <HeaderWithTabs :current-tab="currentTab" @update:selectedView="updateSelectedView" />
      <div class="tab-content">
        <keep-alive>
          <component :is="selectedView" v-bind="selectedProps"></component>
        </keep-alive>
      </div>
      <ReportPermissionDialog
        data-test="report-permissions-dialog"
        v-if="shouldShowReportSharingDialog"
        :refresh-page-on-change="true"
      />
    </div>
    <div v-else>
      <LoadingPage />
    </div>
  </main>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, provide, watch, watchEffect } from "vue";
import axios, { AxiosError } from "axios";
import ReportPermissionDialog from "../components/report-permission-dialog.vue";
import ReportPermissionDialogModel from "../components/report-permission-dialog-model";
import localStorageService from "../services/utilities/local-storage-service";
import { TeamApiModel, TeamSettingsApiModel, UserTeamPermissionsApiModel } from "../api-models";
import { csContext } from "../services/cs-context";
import HeaderWithTabs from "../components/home/header-with-tabs.vue";
import TeamDashboard from "./team/team-dashboard.vue";
import TeamLeadArea from "./team/team-lead-area.vue";
import MyDashboard from "./my-journey/my-dashboard.vue";
import MyDevelopment from "./my-journey/my-development.vue";
import { settleAll } from "../services/utilities/settle-all";
import LoadingPage from "./loading-page.vue";

const homeViews = ["TeamDashboard", "TeamLeadArea", "MyDashboard", "MyDevelopment"] as const;

export type HomeView = (typeof homeViews)[number];

const isLoading = ref(true);

// Injected data
const currentTeam = ref<TeamApiModel | null>(csContext.currentTeam);
provide("currentTeam", currentTeam);

const teamUserPermissions = ref<UserTeamPermissionsApiModel>();
provide("teamUserPermissions", teamUserPermissions);

const teamSettingsModel = ref<TeamSettingsApiModel>();
provide("teamSettingsModel", teamSettingsModel);

const userIsTeamLeader = computed(() => !!teamUserPermissions.value?.isLeader);
provide("userIsTeamLeader", userIsTeamLeader);

const teamModel = ref<TeamApiModel>();
provide("teamModel", teamModel);

const isTeamGoalsSet = ref<boolean>();
provide("isTeamGoalsSet", isTeamGoalsSet);

// selected tab
const viewQueryParam = new URLSearchParams(window.location.search).get("view") as HomeView;

const isValidViewQueryParam = (view: string): view is HomeView => {
  return homeViews.includes(view as HomeView);
};

const HOMEVIEW_KEY = "HomeView";

const currentTab = ref<HomeView>(
  viewQueryParam && isValidViewQueryParam(viewQueryParam)
    ? viewQueryParam
    : (localStorageService.get(HOMEVIEW_KEY) as HomeView) ?? "MyDevelopment",
);

const updateSelectedView = (view: HomeView) => {
  currentTab.value = view;
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);
  window.history.pushState({}, "", url.toString());
};

watch(
  currentTab,
  () => {
    localStorageService.save(HOMEVIEW_KEY, currentTab.value ?? "");
  },
  { immediate: true },
);
const components = {
  TeamDashboard,
  TeamLeadArea,
  MyDashboard,
  MyDevelopment,
};

const componentProps = ref({
  TeamDashboard: {
    id: currentTeam.value?.id,
    isTeamLeader: userIsTeamLeader,
    teamSettingsModel,
    teamModel,
  },
});

const selectedProps = computed(() => componentProps.value[currentTab.value] || {});

const selectedView = computed(() => components[currentTab.value]);

const reportPermissionDialogModel = ref<ReportPermissionDialogModel | null>(null);

const shouldShowReportSharingDialog = computed(
  () => !!reportPermissionDialogModel.value?.ids?.length,
);

onMounted(async () => {
  if (!currentTeam.value) {
    isLoading.value = false;
    return;
  }

  setTimeout(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, 50);

  const promises: Promise<void>[] = [];

  await axios
    .get(`/api/TeamSettings/${currentTeam.value?.id}`)
    .then((resp) => {
      const data = resp.data as TeamSettingsApiModel;
      teamSettingsModel.value = data;
      return data;
    })
    .catch((e: AxiosError) => {
      if (e.response?.status === 403) {
        window.location.href = "/ErrorPages/NotAuthorised";
      } else if (e.response?.status === 404) {
        window.location.href = "/ErrorPages/NotFound";
      } else {
        window.location.href = "/ErrorPages/Error";
      }
    });

  promises.push(
    axios.get(`/api/teamuserpermissions/${currentTeam.value?.id}`).then((resp) => {
      teamUserPermissions.value = resp.data as UserTeamPermissionsApiModel;
    }),
  );

  promises.push(
    axios.get(`/api/teams/${currentTeam.value?.id}`).then((resp) => {
      teamModel.value = resp.data as TeamApiModel;
    }),
  );

  promises.push(
    axios.get("/api/ReportPermissions/").then((resp) => {
      reportPermissionDialogModel.value = resp.data as ReportPermissionDialogModel;
    }),
  );
  promises.push(
    axios.get<boolean>(`/api/teamgoals/isTeamGoalsSet/${currentTeam.value?.id}`).then((resp) => {
      isTeamGoalsSet.value = resp.data;
    }),
  );

  await settleAll(promises);
  isLoading.value = false;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="less">
@import (reference) "../styles/bootstrap/variables.less";

.team-area {
  background-color: @off-white;
}

.green-area {
  background-color: #ffffff;
}

@media (max-width: @screen-xxs-max) {
  .team-area {
    padding-top: 0 !important;
  }
}
</style>
